import { useState, useEffect } from 'react';
import { SignedIn, SignedOut, useUser, useClerk, useAuth } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import DashboardPage from './pages/DashboardPage';
import StudiosPage from './pages/StudiosPage';
import UsersPage from './pages/UsersPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminGuidePage from './pages/AdminGuidePage';
import OutreachPlaybook from './pages/OutreachPlaybook';
import SettingsPage from './pages/SettingsPage';
import RolesPage from './pages/RolesPage';
import LeadsPage from './pages/LeadsPage';
import DiscountsPage from './pages/DiscountsPage';
import AdminLoginPage from './pages/AdminLoginPage';
import RequestAccessPage from './pages/RequestAccessPage';
import { DataModeProvider } from './contexts/DataModeContext';
import { PermissionsProvider, usePermissions, PageId } from './contexts/PermissionsContext';
import { NotificationsProvider } from './contexts/NotificationsContext';

type UserRole = 'admin' | 'marketing' | 'user';

function useUserRole(): { role: UserRole | null; isLoaded: boolean } {
  const { user, isLoaded } = useUser();

  if (!isLoaded) return { role: null, isLoaded: false };

  const role = user?.publicMetadata?.role as UserRole | undefined;
  return { role: role || 'user', isLoaded: true };
}


function AccessDenied() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const role = user?.publicMetadata?.role as string | undefined;
  const requestedAccess = (user?.unsafeMetadata as { requestedAccess?: boolean })?.requestedAccess;
  // Show pending if role is 'pending' OR if user came through request-access flow (handles race condition)
  const isPendingApproval = role === 'pending' || requestedAccess === true;

  // Show pending approval message for 'new' role users
  if (isPendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-2">Access Pending</h1>
          <p className="text-stone-500 dark:text-stone-400 mb-4">
            Your account has been created. An administrator will review your request and grant you access soon. You can close this tab and come back later.
          </p>
          <div className="bg-stone-100 dark:bg-stone-800/50 rounded-xl p-4 mb-4">
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-1">Signed in as</p>
            <p className="text-stone-800 dark:text-stone-200 font-medium">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
          <button
            onClick={() => signOut()}
            className="px-4 py-2 bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300 rounded-lg hover:bg-stone-300 dark:hover:bg-stone-700 transition-colors font-medium"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
      <div className="text-center p-8 max-w-md">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-2">Access Denied</h1>
        <p className="text-stone-500 dark:text-stone-400 mb-6">
          This portal is invite-only. If you need access, ask an administrator to send you an invitation.
        </p>
        <button
          onClick={() => signOut()}
          className="px-4 py-2 bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300 rounded-lg hover:bg-stone-300 dark:hover:bg-stone-700 transition-colors font-medium"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
      <div className="animate-spin w-8 h-8 border-4 border-sage border-t-transparent rounded-full" />
    </div>
  );
}

function PortalGuard({ children }: { children: React.ReactNode }) {
  const { role, isLoaded: roleLoaded } = useUserRole();
  const { permissions, isLoading: permissionsLoading } = usePermissions();

  if (!roleLoaded || permissionsLoading) return <LoadingSpinner />;

  // Check if the user's role has any permissions configured
  const rolePermissions = permissions?.[role || ''];
  const hasAccess = rolePermissions && rolePermissions.pages.length > 0;

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

// Map page IDs to route components
const PAGE_COMPONENTS: Record<PageId, { path: string; element: React.ReactNode }> = {
  dashboard: { path: '/', element: <DashboardPage /> },
  studios: { path: '/studios', element: <StudiosPage /> },
  users: { path: '/users', element: <UsersPage /> },
  subscriptions: { path: '/subscriptions', element: <SubscriptionsPage /> },
  discounts: { path: '/discounts', element: <DiscountsPage /> },
  leads: { path: '/leads', element: <LeadsPage /> },
  analytics: { path: '/analytics', element: <AnalyticsPage /> },
  guide: { path: '/guide', element: <AdminGuidePage /> },
  playbook: { path: '/playbook', element: <OutreachPlaybook /> },
  roles: { path: '/roles', element: <RolesPage /> },
  settings: { path: '/settings', element: <SettingsPage /> },
};

function AppLayout() {
  const { role } = useUserRole();
  const { getAllowedPages, getDefaultPage } = usePermissions();

  // Desktop sidebar state (collapsible)
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Mobile sidebar state (drawer)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin_dark_mode');
    return saved === 'true';
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('admin_dark_mode', String(darkMode));
  }, [darkMode]);

  // Get allowed pages for current role from database
  const allowedPages = getAllowedPages(role || '');
  const configuredDefaultPage = getDefaultPage(role || '');

  // Ensure default page is actually accessible - if not, use first allowed page
  const defaultPageId = Object.entries(PAGE_COMPONENTS).find(([_, config]) => config.path === configuredDefaultPage)?.[0] as PageId | undefined;
  const isDefaultPageAllowed = defaultPageId && allowedPages.includes(defaultPageId);
  const firstAllowedPage = allowedPages[0] ? PAGE_COMPONENTS[allowedPages[0]]?.path : '/';
  const defaultPage = isDefaultPageAllowed ? configuredDefaultPage : firstAllowedPage;

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex">
      {/* Mobile Sidebar (drawer) */}
      <Sidebar
        isOpen={mobileSidebarOpen}
        onToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        userRole={role}
        isMobile={true}
        allowedPages={allowedPages}
      />
      {/* Desktop Sidebar (collapsible) */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        userRole={role}
        isMobile={false}
        allowedPages={allowedPages}
      />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <Header
          onMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode(!darkMode)}
        />
        <main className="flex-1 p-4 lg:p-6 pt-22 overflow-y-auto">
          <Routes>
            {/* Dynamically render routes based on permissions from database */}
            {allowedPages.map((pageId) => {
              const pageConfig = PAGE_COMPONENTS[pageId];
              if (!pageConfig) return null;
              return (
                <Route
                  key={pageId}
                  path={pageConfig.path}
                  element={pageConfig.element}
                />
              );
            })}
            {/* Redirect unknown routes to default page */}
            <Route path="*" element={<Navigate to={defaultPage} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  const { isLoaded } = useAuth();
  const [darkMode] = useState(() => {
    const saved = localStorage.getItem('admin_dark_mode');
    return saved === 'true';
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Wait for Clerk to fully load before showing anything
  // This prevents the login page from flickering while Clerk determines auth state
  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <PermissionsProvider>
      <DataModeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public route for requesting access */}
            <Route path="/request-access" element={<RequestAccessPage />} />
            {/* All other routes require auth check */}
            <Route path="/*" element={
              <>
                <SignedIn>
                  <NotificationsProvider>
                    <PortalGuard>
                      <AppLayout />
                    </PortalGuard>
                  </NotificationsProvider>
                </SignedIn>
                <SignedOut>
                  <AdminLoginPage isDarkMode={darkMode} />
                </SignedOut>
              </>
            } />
          </Routes>
        </BrowserRouter>
      </DataModeProvider>
    </PermissionsProvider>
  );
}

export default App;
